import React, { useState, useEffect, useRef } from 'react';
import { useUser, SignInButton } from '@clerk/clerk-react';
import { useUserApplication } from '../../context/UserApplicationContext';
import { useTranslation } from 'react-i18next';

// Import Dashboard layout components
import DashboardHeader from './layout/DashboardHeader';
import Sidebar from './layout/Sidebar';

// Import Home component
import DashboardHome from './Home/DashboardHome';
import Settings from './Settings/Settings';
import ClientMessagesSection from './Messages/ClientMessagesSection';

// Import Portfolio components
import ApplicationPortfolio from './Portfolio/ApplicationPortfolio';

// Import Payments (Quote & Payment) section — replaces the filing wizard
import PaymentsSection from './Payments/PaymentsSection';

// Valid dashboard sections (URL hash → section)
const VALID_SECTIONS = ['home', 'portfolio', 'payments', 'renewals', 'settings', 'messages'];

export const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  // Access Clerk user and authentication state
  const { isSignedIn, isLoaded, user } = useUser();
  const userName = isSignedIn && user ? user.firstName || user.username : null;

  // Track the current dashboard section (home, portfolio, payments, etc.)
  const [activeSection, setActiveSection] = useState('home');
  // Mobile sidebar drawer (hamburger in the header, <768px only)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentApplication } = useUserApplication();

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (!sidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  // A client arriving with no specific destination and an unpaid quote lands
  // on Quote & Payment (covers every sign-in path, incl. accounts resolved
  // rather than created by the quote flow). Runs once per mount so normal
  // navigation is never hijacked afterwards.
  const autoRoutedRef = useRef(false);
  useEffect(() => {
    if (autoRoutedRef.current) return;
    if (window.location.hash) return; // explicit destination wins
    if (
      currentApplication &&
      currentApplication.status === 'quote_sent' &&
      currentApplication.paymentStatus !== 'paid'
    ) {
      autoRoutedRef.current = true;
      setActiveSection('payments');
      window.location.hash = 'payments';
    }
  }, [currentApplication]);

  // Handle URL hash for section selection (e.g. #payments)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove #
      // Old wizard links/emails deep-link to #ipfiling — redirect to Payments
      if (hash === 'ipfiling' || hash.startsWith('ipfiling?')) {
        setActiveSection('payments');
        return;
      }
      const section = hash.split('?')[0];
      if (VALID_SECTIONS.includes(section)) {
        setActiveSection(section);
      }
    };

    // Set initial section from hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Handle section change from sidebar
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSidebarOpen(false); // close the mobile drawer on navigation
    window.location.hash = section;
  };

  // Create auth redirect URL for Clerk modal
  const getAuthRedirectUrl = () => {
    return window.location.origin + '/dashboard';
  };

  // Auto-trigger Clerk modal when component mounts and user isn't signed in
  useEffect(() => {
    // Only try to show the modal if Clerk auth is fully loaded
    // and the user is not already signed in
    if (isLoaded && !isSignedIn) {
      // Find the modal-trigger button and programmatically click it
      const triggerButton = document.getElementById('clerk-auth-trigger');
      if (triggerButton) {
        triggerButton.click();
      }
    }
  }, [isSignedIn, isLoaded]);

  // Handle Clerk modal being closed without authentication
  useEffect(() => {
    // Only redirect if user explicitly closes the modal using the X button
    const handleClerkModalClosed = (event) => {
      // Check if the user is not signed in and clicked the close button
      // The close button has a specific class 'cl-modalCloseButton'
      if (!isSignedIn &&
          (event.target.classList.contains('cl-modalCloseButton') ||
           event.target.closest('.cl-modalCloseButton'))) {
        window.location.href = '/';
      }
    };

    document.addEventListener('click', handleClerkModalClosed);
    return () => {
      document.removeEventListener('click', handleClerkModalClosed);
    };
  }, [isSignedIn]);

  return (
    <>
        <div className="min-h-screen flex flex-col">
          {/* Dashboard Header - Fixed */}
          <DashboardHeader
            userName={userName}
            onMenuClick={() => setSidebarOpen((open) => !open)}
          />

          {/* Main container with padding-top to account for fixed header */}
          <div className="flex flex-1 overflow-hidden mt-16">
            {/* Sidebar Navigation — static column on md+, overlay drawer on mobile */}
            <Sidebar
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              mobileOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />

            {/* Main Content Area */}
            <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
              <div className="max-w-6xl mx-auto">
                {/* Render content based on active section */}
                {activeSection === 'home' ? (
                  <DashboardHome onNavigate={handleSectionChange} />
                ) : activeSection === 'portfolio' ? (
                  // Portfolio Section - Show application portfolio
                  <div className="bg-white rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                      <h1 className="text-2xl font-bold text-gray-900">My IP Portfolio</h1>
                      <p className="text-gray-600 mt-1">Manage your trademark applications and intellectual property</p>
                    </div>
                    <ApplicationPortfolio />
                  </div>
                ) : activeSection === 'payments' ? (
                  // Quote & Payment Section
                  <div>
                    <div className="mb-6">
                      <h1 className="text-2xl font-bold text-gray-900">{t('payments.sectionTitle', 'Quote & Payment')}</h1>
                      <p className="text-gray-600 mt-1">{t('payments.sectionSubtitle', 'Review your quote, sign the declaration, and complete payment')}</p>
                    </div>
                    <PaymentsSection />
                  </div>
                ) : activeSection === 'settings' ? (
                  // Settings Section
                  <Settings />
                ) : activeSection === 'messages' ? (
                  // Messages Section
                  <ClientMessagesSection />
                ) : (
                  // Other sections (renewals) - Coming soon placeholder
                  <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <div className="max-w-md mx-auto">
                      <div className="text-6xl mb-4">🚧</div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon</h2>
                      <p className="text-gray-600">
                        This section is currently under development. Please check back later!
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </main>
          </div>

          {/* Hidden button to trigger Clerk modal - using data-redirect-url to avoid DOM warnings */}
          <div className="hidden">
            <SignInButton mode="modal" data-redirect-url={getAuthRedirectUrl()}>
              <button id="clerk-auth-trigger">Sign In</button>
            </SignInButton>
          </div>
        </div>
    </>
  );
};

export default Dashboard;
